import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AllCategory, getAllCatagory } from "../../../api/category.api";
import { PatchMenuBody, patchMenuByID, getMenuByID } from "../../../api/menu.api";
import { uploadMenuImage } from "../../../api/image.api";
import { fileToResizedDataUrl, ikThumb } from "../../../utils/image";
import { State } from "../../../reducer";
import CropImageModal from "../../../components/CropImageModal";
import AvailabilityEditor from "../../../components/AvailabilityEditor";
import DailyStockEditor from "../../../components/DailyStockEditor";
import SaucesEditor from "../../../components/SaucesEditor";

function minToTime(min: number | null | undefined): string {
  if (min == null) return "";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function timeToMin(t: string): number | null {
  if (!t) return null;
  const [h, m] = t.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

// Warm "café" palette tokens — explicit so this page doesn't follow
// the v2 monochrome theme used everywhere else.
const C = {
  page:      "#FAF6EF",
  surface:   "#FFFFFF",
  surfaceAlt:"#F5EBD8",
  border:    "#EFE0C7",
  borderSoft:"#F4E7CF",
  text:      "#2A1810",
  textMid:   "#7A6857",
  textMute:  "#B5A28C",
  accent:    "#C2410C",
  accentHover: "#9A3412",
  accentSoft:"#FDF1E1",
  accentSoftText: "#9A3412",
  success:   "#15803D",
  successSoft: "#DCFCE7",
  danger:    "#B91C1C",
  dangerSoft:"#FEE4E2",
};

function EditMenu() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const menu = useSelector((state: State) => state.menu);
  const { menuId } = useParams();
  const menuIdAsNumber = menuId ? parseInt(menuId) : menu.menu_id;

  const [categories, setCategories] = useState<AllCategory>([]);
  const [name, setName] = useState(menu.name);
  const [description, setDescription] = useState(menu.description);
  const [isActive, setIsActive] = useState<string>(menu.isActive || "active");
  const [selectedCategory, setSelectedCategory] = useState<number>(menu.category_id);
  const [imageUrl, setImageUrl] = useState<string>(menu.image || "");
  const [imageFileId, setImageFileId] = useState<string>(menu.imageFileId || "");
  const [originalSrc, setOriginalSrc] = useState<string>("");
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const openPicker = () => fileInputRef.current?.click();
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string>("");
  const [availDays, setAvailDays] = useState<number[]>([]);
  const [availFrom, setAvailFrom] = useState<string>("");
  const [availTo, setAvailTo]     = useState<string>("");
  const [dailyStock, setDailyStock] = useState<number | null>(null);
  const [sauces, setSauces] = useState<string[] | null>(null);

  const lockedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategory && c.categoryFor === "dessert") ?? null,
    [categories, selectedCategory],
  );

  useEffect(() => {
    getAllCatagory().then((all) => {
      setCategories(all.filter((c) => c.isAvailable === "available"));
    });
    if (menuIdAsNumber) {
      getMenuByID(menuIdAsNumber).then((m) => {
        if (!menu.name || menu.menu_id !== menuIdAsNumber) {
          setName(m.name);
          setDescription(m.description ?? "");
          setIsActive(m.isActive);
          setSelectedCategory(m.category?.id ?? selectedCategory);
          setImageUrl(m.image || "");
          setImageFileId(m.imageFileId || "");
        }
        setAvailDays(m.availableDays ?? []);
        setAvailFrom(minToTime(m.availableFromMin));
        setAvailTo(minToTime(m.availableToMin));
        setDailyStock(m.dailyStock ?? null);
        setSauces(m.sauces ?? null);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuIdAsNumber]);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v.charAt(0) === " ") {
      setNameError("Name cannot start with a space");
      return;
    }
    setName(v);
    setNameError("");
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB");
      return;
    }
    try {
      const base64 = await fileToResizedDataUrl(file, 2400, 0.95);
      setOriginalSrc(base64);
      setCropSrc(base64);
    } catch (err) {
      console.error(err);
      toast.error("Could not read image");
    }
    e.target.value = "";
  };

  const handleCropConfirm = async (cropped: string) => {
    setCropSrc(null);
    setUploading(true);
    try {
      const result = await uploadMenuImage({ file: cropped, replaceFileId: imageFileId || undefined });
      if (result?.url) {
        setImageUrl(result.url);
        setImageFileId(result.fileId);
      } else {
        toast.error("Image upload failed");
      }
    } catch (err) {
      console.error(err);
      // Show why it failed (size, format, session) instead of a generic line.
      toast.error(err instanceof Error && err.message ? err.message : "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const isFormValid = !!name && !!selectedCategory && !nameError && !uploading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      toast("Please enter a name and select a category");
      return;
    }
    setSubmitting(true);
    try {
      const body: PatchMenuBody = {
        name,
        description: description || "-",
        category_id: selectedCategory,
        isActive,
        isAvailable: "available",
        ...(imageUrl ? { image: imageUrl, imageFileId } : { image: "", imageFileId: "" }),
        availableDays: availDays.length > 0 ? availDays : null,
        availableFromMin: timeToMin(availFrom),
        availableToMin: timeToMin(availTo),
        dailyStock,
        sauces,
      };
      await patchMenuByID(menuIdAsNumber, body);
      dispatch({ type: "CLEAREDIT" });
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error("Could not save menu");
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full h-[52px] px-4 rounded-2xl text-base focus:outline-none transition-colors";
  const inputStyle = {
    backgroundColor: C.surface,
    border: `1.5px solid ${C.border}`,
    color: C.text,
  } as const;
  const focusRing = "focus:shadow-[0_0_0_4px_rgba(194,65,12,0.12)]";

  return (
    <div className="min-h-screen font-sans pb-[120px]" style={{ background: `linear-gradient(180deg, ${C.page} 0%, #F2E5CC 100%)` }}>
      {/* Sticky top bar */}
      <header
        className="sticky top-0 z-10 px-5 pt-5 pb-4 backdrop-blur"
        style={{ background: "rgba(250,246,239,0.85)", borderBottom: `1px solid ${C.borderSoft}` }}
      >
        <div className="max-w-[760px] mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="w-[42px] h-[42px] rounded-full flex items-center justify-center transition active:scale-95"
            style={{ backgroundColor: C.surfaceAlt, color: C.text }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: C.accentSoftText }}>Editing</p>
            <h1
              className="font-display font-black text-[22px] leading-tight truncate"
              style={{ color: C.text, letterSpacing: "-0.02em" }}
            >
              {name || "Menu"}
            </h1>
          </div>
          <span
            className="hidden sm:inline-flex items-center gap-1.5 h-[28px] px-3 rounded-full text-[11px] font-bold uppercase tracking-wider"
            style={
              isActive === "active"
                ? { backgroundColor: C.successSoft, color: C.success }
                : { backgroundColor: C.surfaceAlt, color: C.textMid }
            }
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isActive === "active" ? C.success : C.textMute }} />
            {isActive}
          </span>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="px-5 pt-6 space-y-5 max-w-[760px] mx-auto">
        {/* Hero image */}
        <section
          className="rounded-3xl overflow-hidden"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.borderSoft}`, boxShadow: "0 6px 28px -16px rgba(194,65,12,0.18)" }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={openPicker}
            className="relative block w-full aspect-[16/10] overflow-hidden text-left group"
            style={{ background: imageUrl ? "transparent" : `linear-gradient(135deg, ${C.surfaceAlt} 0%, #EAD9BE 100%)` }}
          >
            {imageUrl ? (
              <>
                <img
                  src={imageUrl.startsWith("data:") ? imageUrl : ikThumb(imageUrl, 1200)}
                  alt=""
                  className="w-full h-full object-cover pointer-events-none transition-transform duration-500 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-x-0 bottom-0 h-20 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(42,24,16,0.55) 100%)" }} />
                <span
                  className="absolute right-4 bottom-4 inline-flex items-center gap-1.5 px-3 h-[34px] rounded-full text-xs font-semibold"
                  style={{ backgroundColor: "rgba(255,255,255,0.95)", color: C.text }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                  </svg>
                  Change photo
                </span>
              </>
            ) : uploading ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" style={{ color: C.textMid }}>
                <svg className="w-7 h-7 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <span className="text-xs font-semibold">Uploading…</span>
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: C.surface, boxShadow: "0 6px 16px -8px rgba(42,24,16,0.25)" }}>
                  <svg className="w-7 h-7" fill="none" stroke={C.accent} strokeWidth="1.8" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16l5-5 4 4 3-3 6 6M3 8a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                  </svg>
                </div>
                <span className="font-display font-bold text-base" style={{ color: C.text }}>Add a photo</span>
                <span className="text-[11px]" style={{ color: C.textMid }}>PNG · JPG · WEBP · max 5 MB</span>
              </div>
            )}
          </button>

          {imageUrl && (
            <div className="grid grid-cols-3 gap-2 p-3">
              <button
                type="button"
                onClick={() => setCropSrc(originalSrc || imageUrl)}
                className="h-[44px] rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 transition"
                style={{ backgroundColor: C.accentSoft, color: C.accentSoftText }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 2v4H2m16 0h-4V2M6 22v-4H2m16 0h-4v4M3 9v6a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2z" />
                </svg>
                Crop
              </button>
              <button
                type="button"
                onClick={openPicker}
                className="h-[44px] rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 transition"
                style={{ backgroundColor: C.surfaceAlt, color: C.text }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4-4a3 3 0 014 0l4 4m-2-2l1-1a3 3 0 014 0l3 3M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                </svg>
                Replace
              </button>
              <button
                type="button"
                onClick={() => { setImageUrl(""); setImageFileId(""); setOriginalSrc(""); }}
                className="h-[44px] rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 transition"
                style={{ backgroundColor: C.dangerSoft, color: C.danger }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
                Remove
              </button>
            </div>
          )}
        </section>

        {/* Basics: name + description */}
        <section
          className="rounded-3xl p-5"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.borderSoft}` }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1 h-4 rounded-full" style={{ backgroundColor: C.accent }} />
            <p className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: C.accentSoftText }}>Details</p>
          </div>

          <label htmlFor="menu-name" className="block font-display font-bold text-sm mb-2" style={{ color: C.text }}>
            Name <span style={{ color: C.danger }}>*</span>
          </label>
          <input
            id="menu-name"
            type="text"
            value={name}
            maxLength={50}
            onChange={handleNameChange}
            className={`${inputBase} ${focusRing}`}
            style={inputStyle}
            placeholder="e.g. Iced Latte"
          />
          {nameError && <p className="text-xs mt-1.5" style={{ color: C.danger }}>{nameError}</p>}

          <label htmlFor="menu-desc" className="block font-display font-bold text-sm mt-4 mb-2" style={{ color: C.text }}>
            Description
          </label>
          <input
            id="menu-desc"
            type="text"
            value={description}
            maxLength={150}
            onChange={(e) => setDescription(e.target.value)}
            className={`${inputBase} ${focusRing}`}
            style={inputStyle}
            placeholder="Short tagline (optional)"
          />
        </section>

        {/* Status */}
        <section
          className="rounded-3xl p-5"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.borderSoft}` }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1 h-4 rounded-full" style={{ backgroundColor: C.accent }} />
            <p className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: C.accentSoftText }}>Status</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {(["active", "inactive"] as const).map((s) => {
              const on = isActive === s;
              const isOn = s === "active";
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setIsActive(s)}
                  className="h-[56px] rounded-2xl font-display font-bold text-sm transition flex items-center justify-center gap-2"
                  style={
                    on
                      ? isOn
                        ? { backgroundColor: C.success, color: "#FFFFFF" }
                        : { backgroundColor: C.text, color: "#FFFFFF" }
                      : { backgroundColor: C.surfaceAlt, color: C.textMid, border: `1.5px solid transparent` }
                  }
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: on ? "#FFFFFF" : (isOn ? C.success : C.textMute),
                    }}
                  />
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              );
            })}
          </div>
        </section>

        {/* Availability */}
        <section
          className="rounded-3xl p-5"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.borderSoft}` }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1 h-4 rounded-full" style={{ backgroundColor: C.accent }} />
            <p className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: C.accentSoftText }}>Availability</p>
          </div>
          <AvailabilityEditor
            days={availDays}
            setDays={setAvailDays}
            fromTime={availFrom}
            toTime={availTo}
            setFromTime={setAvailFrom}
            setToTime={setAvailTo}
          />
        </section>

        {/* Daily stock */}
        <section
          className="rounded-3xl p-5"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.borderSoft}` }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1 h-4 rounded-full" style={{ backgroundColor: C.accent }} />
            <p className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: C.accentSoftText }}>Daily stock</p>
          </div>
          <DailyStockEditor value={dailyStock} onChange={setDailyStock} />
        </section>

        {/* Sauces */}
        <section
          className="rounded-3xl p-5"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.borderSoft}` }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1 h-4 rounded-full" style={{ backgroundColor: C.accent }} />
            <p className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: C.accentSoftText }}>Sauce options</p>
          </div>
          <SaucesEditor value={sauces} onChange={setSauces} />
        </section>

        {/* Category */}
        <section
          className="rounded-3xl p-5"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.borderSoft}` }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1 h-4 rounded-full" style={{ backgroundColor: C.accent }} />
            <p className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: C.accentSoftText }}>
              Category {!lockedCategory && <span style={{ color: C.danger }}>*</span>}
            </p>
          </div>

          {lockedCategory ? (
            <div
              className="px-4 h-[56px] rounded-2xl flex items-center justify-between"
              style={{ backgroundColor: C.accentSoft, border: `1.5px solid ${C.border}` }}
            >
              <span className="font-display font-bold text-base" style={{ color: C.text }}>{lockedCategory.name}</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: C.accentSoftText }}>Dessert</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {categories.map((c) => {
                const on = selectedCategory === c.id;
                return (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 px-4 h-[56px] rounded-2xl cursor-pointer transition"
                    style={
                      on
                        ? { backgroundColor: C.accentSoft, border: `1.5px solid ${C.accent}` }
                        : { backgroundColor: C.page, border: `1.5px solid ${C.borderSoft}` }
                    }
                  >
                    <input
                      type="radio"
                      name="category"
                      value={c.id}
                      checked={on}
                      onChange={() => setSelectedCategory(c.id)}
                      className="sr-only"
                    />
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ border: `2px solid ${on ? C.accent : C.textMute}` }}
                    >
                      {on && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: C.accent }} />}
                    </div>
                    <span className="font-display font-semibold text-base flex-1" style={{ color: C.text }}>{c.name}</span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: C.textMid }}>{c.categoryFor}</span>
                  </label>
                );
              })}
            </div>
          )}
        </section>
      </form>

      {/* Bottom action bar */}
      <div
        className="fixed bottom-0 left-0 right-0 px-5 py-3 z-20"
        style={{
          background: `linear-gradient(180deg, rgba(250,246,239,0) 0%, ${C.page} 28%)`,
        }}
      >
        <div className="max-w-[760px] mx-auto flex gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 h-[52px] rounded-2xl font-bold text-sm transition active:scale-[0.98]"
            style={{ backgroundColor: C.surface, color: C.text, border: `1.5px solid ${C.border}` }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!isFormValid || submitting}
            onClick={handleSubmit}
            className="flex-[2] h-[52px] rounded-2xl font-bold text-sm transition active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: C.accent,
              color: "#FFFFFF",
              boxShadow: "0 8px 24px -8px rgba(194,65,12,0.45)",
            }}
            onMouseEnter={(e) => { if (!submitting && isFormValid) (e.currentTarget.style.backgroundColor = C.accentHover); }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = C.accent; }}
          >
            {submitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3" />
                  <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                Saving…
              </>
            ) : (
              <>
                Save changes
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m-4-4l4 4-4 4" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>

      {cropSrc && (
        <CropImageModal
          src={cropSrc}
          aspect={16 / 10}
          onCancel={() => setCropSrc(null)}
          onConfirm={handleCropConfirm}
        />
      )}

      <ToastContainer />
    </div>
  );
}

export default EditMenu;
